// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState } from 'react';
import { AlertTriangle, Info, ShieldAlert, X } from 'lucide-react';

export const Modal = ({ open, onClose, children, width = 'max-w-[520px]', backdropClose = true, title, sub, footer }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] bg-[#1F2937]/40 flex items-center justify-center p-4" onClick={backdropClose ? onClose : undefined}>
      <div className={`bg-white border border-[#E6E4E1] rounded-[12px] w-full ${width} shadow-xl max-h-[90vh] flex flex-col`} onClick={(e) => e.stopPropagation()}>
        {title && (
          <div className="px-5 pt-5 pb-3 border-b border-[#EEEDEA] flex items-start justify-between gap-3 flex-shrink-0">
            <div className="min-w-0">
              <div className="text-[14.5px] font-semibold text-[#1F2937] leading-tight">{title}</div>
              {sub && <div className="text-[11.5px] text-[#6B7280] mt-0.5">{sub}</div>}
            </div>
            <button onClick={onClose} className="w-7 h-7 rounded-md hover:bg-[#F2F1ED] flex items-center justify-center flex-shrink-0" title="Close"><X className="w-3.5 h-3.5 text-[#6B7280]" strokeWidth={2} /></button>
          </div>
        )}
        <div className={`${title ? 'px-5 py-4' : ''} flex-1 overflow-y-auto`}>
          {children}
        </div>
        {footer && (
          <div className="px-5 py-3 border-t border-[#EEEDEA] bg-[#FBFAF7] rounded-b-[12px] flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

// Confirm dialog — for any irreversible or sensitive action

export const ConfirmDialog = ({ open, onClose, title, body, confirmLabel = 'Confirm', tone = 'neutral', onConfirm, requireText, approval, requireReason }) => {
  const [text, setText] = useState('');
  const [reason, setReason] = useState('');
  // Adjust state during render (not in an effect) when `open` changes, so a closed
  // dialog's stale text/reason never flashes for a frame before an effect clears it —
  // see react.dev "Adjusting some state when a prop changes".
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open) { setText(''); setReason(''); }
  }
  if (!open) return null;
  const disabled = (requireText ? text !== requireText : false) || (requireReason ? !reason.trim() : false);
  const btnTone = tone === 'danger' ? 'bg-[#9B1F1F] hover:bg-[#7F1818] text-white' : 'bg-[#1F2937] hover:bg-[#111827] text-white';
  return (
    <Modal open={open} onClose={onClose}>
      <div className="px-5 pt-5 pb-3">
        <div className={`w-9 h-9 rounded-full ${tone === 'danger' ? 'bg-[#FEE2E2] text-[#9B1F1F]' : 'bg-[#F2F1ED] text-[#1F2937]'} flex items-center justify-center mb-3`}>
          {tone === 'danger' ? <AlertTriangle className="w-4 h-4" strokeWidth={2} /> : <Info className="w-4 h-4" strokeWidth={2} />}
        </div>
        <div className="text-[15px] font-semibold text-[#1F2937] mb-1.5">{title}</div>
        <div className="text-[12.5px] text-[#4B5563] leading-relaxed">{body}</div>
        {approval && (
          <div className="mt-3 px-3 py-2 bg-[#FEF3C7] border border-[#FDE68A] rounded-md text-[11.5px] text-[#8A5A12] flex items-start gap-2">
            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{approval}</span>
          </div>
        )}
        {requireText && (
          <div className="mt-4">
            <div className="text-[11.5px] text-[#6B7280] mb-1.5">Type <span className="font-mono font-semibold text-[#1F2937]">{requireText}</span> to confirm.</div>
            <input value={text} onChange={(e) => setText(e.target.value)} className="w-full h-9 px-3 text-[12.5px] bg-white border border-[#E6E4E1] rounded-md focus:outline-none focus:border-[#1F2937] font-mono" />
          </div>
        )}
        {requireReason && (
          <div className="mt-4">
            <div className="text-[11.5px] text-[#6B7280] mb-1.5">{typeof requireReason === 'string' ? requireReason : 'Reason (shown to the organizer):'}</div>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="What needs to change before this can be approved?"
              className="w-full px-3 py-2 text-[12.5px] bg-white border border-[#E6E4E1] rounded-md focus:outline-none focus:border-[#1F2937] resize-none"
            />
          </div>
        )}
      </div>
      <div className="px-5 py-3 border-t border-[#EEEDEA] flex items-center justify-end gap-2 bg-[#FBFAF7] rounded-b-[12px]">
        <button onClick={onClose} className="h-8 px-3 rounded-md text-[12px] font-medium text-[#1F2937] hover:bg-[#F2F1ED] border border-[#E6E4E1] bg-white whitespace-nowrap">Cancel</button>
        <button disabled={disabled} onClick={() => { onConfirm && onConfirm(requireReason ? reason.trim() : undefined); onClose(); }} className={`h-8 px-3 rounded-md text-[12px] font-medium whitespace-nowrap ${btnTone} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>{confirmLabel}</button>
      </div>
    </Modal>
  );
};

// useConfirm hook — convenience wrapper
